import { defineStyleConfig } from "@chakra-ui/react";
import { mode } from "@chakra-ui/theme-tools";

import { buttonSizes, focusStyle, textLink } from "@/theme/helpers";
import { theme } from "@/theme/theme";

export const ButtonTheme = defineStyleConfig({
  defaultProps: {
    size: "sm",
  },
  baseStyle: (props) => ({
    textDecoration: "none",
    _hover: {
      textDecoration: "none",
    },
    _focus: {
      boxShadow: focusStyle(props),
    },
  }),
  sizes: {
    "2xs": {
      ...buttonSizes["2xs"],
      px: 2,
      fontSize: "xs",
      fontWeight: "medium",
    },
    xs: {
      ...buttonSizes.xs,
    },
    sm: {
      ...buttonSizes.sm,
    },
    md: {
      ...buttonSizes.md,
    },
    lg: {
      ...buttonSizes.lg,
    },
  },
  variants: {
    ghost: (props) =>
      props.colorScheme === "gray"
        ? {
            color: mode("inherit", "gray.100")(props),
            _hover: {
              bg: mode("gray.50", "gray.750")(props),
            },
            _active: { bg: mode("gray.100", "gray.700")(props) },
          }
        : {},
    ghostDimmed: (props) =>
      props.colorScheme === "gray"
        ? {
            color: mode("gray.500", "gray.500")(props),
            _hover: {
              color: mode("gray.900", "gray.100")(props),
            },
            _active: { bg: mode("gray.100", "gray.700")(props) },
          }
        : {},
    outlineOnHover: (props) => ({
      borderWidth: "1px",
      color: mode("inherit", "gray.100")(props),
      fontWeight: "medium",
      borderColor: "transparent",
      _hover: {
        bg: mode("gray.50", "gray.750")(props),
        borderColor: mode(`gray.200`, `whiteAlpha.300`)(props),
        boxShadow: "sm",
      },
      _active: {
        backgroundColor: mode("gray.50", "gray.800")(props),
        borderColor: mode(`gray.200`, `whiteAlpha.300`)(props),
        boxShadow: "sm",
        bg: mode("gray.100", "gray.700")(props),
      },
    }),
    outline: (props) =>
      props.colorScheme === "gray"
        ? {
            boxShadow: "sm",
            bg: mode("white", "gray.850")(props),
            color: mode("gray.800", "gray.100")(props),
            borderColor: mode("gray.250", "gray.650")(props),
            _hover: {
              bg: mode("gray.50", "gray.750")(props),
              borderColor: mode("gray.300", "gray.600")(props),
            },
            _active: { bg: mode("gray.100", "gray.700")(props) },
          }
        : {
            boxShadow: "sm",
            color: mode(
              `${props.colorScheme}.500`,
              `${props.colorScheme}.300`,
            )(props),
            bg: mode(
              `${props.colorScheme}.50`,
              `${props.colorScheme}.900`,
            )(props),
            borderColor: mode(
              `${props.colorScheme}.400`,
              `${props.colorScheme}.700`,
            )(props),
            _hover: {
              bg: mode(
                `${props.colorScheme}.50`,
                `${props.colorScheme}.900`,
              )(props),
              borderColor: mode(
                `${props.colorScheme}.600`,
                `${props.colorScheme}.600`,
              )(props),
            },
            _active: {
              bg: mode(
                `${props.colorScheme}.100`,
                `${props.colorScheme}.800`,
              )(props),
            },
          },
    solid: (props) =>
      props.colorScheme === "gray"
        ? {
            boxShadow: "sm",
            bg: mode("gray.850", "white")(props),
            color: mode("white", "gray.900")(props),
            _disabled: {
              opacity: 1,
              bg: mode("gray.400", "gray.500")(props),
              color: mode("gray.200", "gray.900")(props),
            },
            _hover: {
              bg: mode("gray.950", "gray.50")(props),
              _disabled: {
                bg: mode("gray.400", "gray.500")(props),
                color: mode("gray.200", "gray.900")(props),
              },
            },
            _active: {
              bg: mode("gray.900", "gray.50")(props),
              _disabled: {
                bg: mode("gray.400", "gray.500")(props),
                color: mode("gray.200", "gray.900")(props),
              },
            },
          }
        : {
            boxShadow: "sm",
            bg: mode(
              `${props.colorScheme}.500`,
              `${props.colorScheme}.500`,
            )(props),
            color: "white",
            _disabled: {
              opacity: 1,
              bg: mode(
                `${props.colorScheme}.400`,
                `${props.colorScheme}.600`,
              )(props),
              color: mode(
                `${props.colorScheme}.100`,
                `${props.colorScheme}.200`,
              )(props),
            },
            _hover: {
              bg: mode(
                `${props.colorScheme}.600`,
                `${props.colorScheme}.400`,
              )(props),
              _disabled: {
                bg: mode(
                  `${props.colorScheme}.400`,
                  `${props.colorScheme}.600`,
                )(props),
                color: mode(
                  `${props.colorScheme}.100`,
                  `${props.colorScheme}.200`,
                )(props),
              },
            },
            _active: {
              bg: mode(
                `${props.colorScheme}.700`,
                `${props.colorScheme}.300`,
              )(props),
              _disabled: {
                bg: mode(
                  `${props.colorScheme}.400`,
                  `${props.colorScheme}.600`,
                )(props),
                color: mode(
                  `${props.colorScheme}.100`,
                  `${props.colorScheme}.200`,
                )(props),
              },
            },
          },
    solidDimmed: (props) =>
      props.colorScheme === "gray"
        ? {
            boxShadow: "sm",
            bg: mode("gray.400", "gray.500")(props),
            color: mode("white", "gray.900")(props),
            _disabled: {
              opacity: 1,
              bg: mode("gray.400", "gray.500")(props),
              color: mode("white", "gray.900")(props),
            },
            _hover: {
              bg: mode("gray.400", "gray.500")(props),
              _disabled: {
                bg: mode("gray.400", "gray.500")(props),
                color: mode("white", "gray.900")(props),
              },
            },
            _active: {
              bg: mode("gray.400", "gray.500")(props),
              _disabled: {
                bg: mode("gray.400", "gray.500")(props),
                color: mode("white", "gray.900")(props),
              },
            },
          }
        : {
            boxShadow: "sm",
            bg: mode(
              `${props.colorScheme}.300`,
              `${props.colorScheme}.900`,
            )(props),
            color: "white",
            _disabled: {
              opacity: 1,
              bg: mode(
                `${props.colorScheme}.300`,
                `${props.colorScheme}.900`,
              )(props),
              color: "white",
            },
            _hover: {
              bg: mode(
                `${props.colorScheme}.300`,
                `${props.colorScheme}.900`,
              )(props),
              _disabled: {
                bg: mode(
                  `${props.colorScheme}.400`,
                  `${props.colorScheme}.800`,
                )(props),
                color: mode("white", "gray.900")(props),
              },
            },
            _active: {
              bg: mode(
                `${props.colorScheme}.300`,
                `${props.colorScheme}.900`,
              )(props),
              _disabled: {
                bg: mode(
                  `${props.colorScheme}.400`,
                  `${props.colorScheme}.800`,
                )(props),
                color: mode("white", "gray.900")(props),
              },
            },
          },
    subtle: (props) =>
      props.colorScheme === "gray"
        ? {
            bg: mode("gray.50", "gray.750")(props),
            _hover: {
              bg: mode("gray.100", "gray.700")(props),
              _disabled: {
                bg: mode("gray.50", "gray.750")(props),
              },
            },
            _active: {
              bg: mode("gray.200", "gray.650")(props),
              _disabled: {
                bg: mode("gray.50", "gray.750")(props),
              },
            },
          }
        : {
            bg: mode(
              `${props.colorScheme}.50`,
              `${props.colorScheme}.900`,
            )(props),
            color: mode(
              `${props.colorScheme}.500`,
              `${props.colorScheme}.100`,
            )(props),
            _hover: {
              bg: mode(
                `${props.colorScheme}.100`,
                `${props.colorScheme}.800`,
              )(props),
              _disabled: {
                bg: mode(
                  `${props.colorScheme}.50`,
                  `${props.colorScheme}.900`,
                )(props),
              },
            },
            _active: {
              bg: mode(
                `${props.colorScheme}.200`,
                `${props.colorScheme}.700`,
              )(props),
              _disabled: {
                bg: mode(
                  `${props.colorScheme}.50`,
                  `${props.colorScheme}.900`,
                )(props),
              },
            },
          },
    input: (props) => ({
      ...theme.components.Input.variants.outline(props).field,
      bg: mode("white", "gray.850")(props),
      _hover: {
        bg: mode("gray.50", "gray.750")(props),
      },
      _active: { bg: mode("gray.100", "gray.700")(props) },
      fontWeight: "normal",
      textAlign: "left",
      justifyContent: "flex-start",
    }),
    link: textLink,
  },
});
